export interface StreakData {
  currentStreak: number;
  longestStreak: number;
  lastActivityDate: string; // ISO date string
}

export interface StreakDisplay {
  currentStreak: number;
  longestStreak: number;
  icon: string;
  color: string;
  title: string;
  message: string;
  isNewRecord: boolean;
}

/**
 * Get streak display information based on current streak
 */
export function getStreakDisplay(streakData: StreakData): StreakDisplay {
  const { currentStreak, longestStreak } = streakData;
  const isNewRecord = currentStreak === longestStreak && currentStreak > 1;

  // Determine icon and color based on streak length
  let icon = "🔥";
  let color = "from-orange-400 to-red-500";
  let title = "Streak";

  if (currentStreak >= 365) {
    icon = "👑";
    color = "from-yellow-300 via-yellow-500 to-orange-600";
    title = "Legendary Streak!";
  } else if (currentStreak >= 180) {
    icon = "💎";
    color = "from-blue-400 via-purple-500 to-pink-600";
    title = "Diamond Streak!";
  } else if (currentStreak >= 90) {
    icon = "⭐";
    color = "from-yellow-400 to-orange-500";
    title = "Amazing Streak!";
  } else if (currentStreak >= 30) {
    icon = "🌟";
    color = "from-purple-400 to-pink-500";
    title = "Great Streak!";
  } else if (currentStreak >= 7) {
    icon = "✨";
    color = "from-cyan-400 to-blue-500";
    title = "Week Streak!";
  } else if (currentStreak >= 3) {
    icon = "🔥";
    color = "from-orange-400 to-red-500";
    title = "Hot Streak!";
  } else if (currentStreak === 0) {
    icon = "🌱";
    color = "from-gray-400 to-gray-600";
    title = "Start Your Streak!";
  }

  // Generate motivational message
  let message = "";
  if (currentStreak === 0) {
    message = "Start your learning journey today!";
  } else if (currentStreak === 1) {
    message = "Great start! Come back tomorrow to build your streak.";
  } else if (currentStreak < 7) {
    message = `${currentStreak} days in a row! Keep it going!`;
  } else if (currentStreak < 30) {
    message = `${currentStreak} day streak! You're on fire!`;
  } else if (currentStreak < 90) {
    message = `${currentStreak} days straight! Incredible dedication!`;
  } else if (currentStreak < 180) {
    message = `${currentStreak} day streak! You're unstoppable!`;
  } else if (currentStreak < 365) {
    message = `${currentStreak} days! This is legendary!`;
  } else {
    message = `${currentStreak} days! You're a true master!`;
  }

  if (isNewRecord) {
    message += " 🎉 New personal record!";
  }

  return {
    currentStreak,
    longestStreak,
    icon,
    color,
    title,
    message,
    isNewRecord,
  };
}

/**
 * Get user's local date in YYYY-MM-DD format
 * This ensures streak calculations are based on the user's timezone
 */
export function getUserLocalDate(): string {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, "0");
  const day = String(now.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

/**
 * Check if streak needs updating based on last activity date
 * Compares dates in YYYY-MM-DD format (timezone-aware)
 */
export function shouldUpdateStreak(lastActivityDate: string): boolean {
  const todayLocal = getUserLocalDate();
  // Extract just the date portion if lastActivityDate is an ISO string
  const lastActivityLocal = lastActivityDate.split("T")[0];

  // Update if last activity was not today (in user's local timezone)
  return todayLocal !== lastActivityLocal;
}

/**
 * Calculate if a streak is at risk of breaking (last activity was yesterday)
 * Uses local timezone for accurate comparison
 */
export function isStreakAtRisk(lastActivityDate: string): boolean {
  const lastActivityLocal = lastActivityDate.split("T")[0];

  // Calculate yesterday in local timezone
  const yesterday = new Date();
  yesterday.setDate(yesterday.getDate() - 1);
  const yesterdayLocal = `${yesterday.getFullYear()}-${String(yesterday.getMonth() + 1).padStart(2, "0")}-${String(yesterday.getDate()).padStart(2, "0")}`;

  return lastActivityLocal === yesterdayLocal;
}

/**
 * Calculate if a streak was broken (last activity was before yesterday)
 * Uses local timezone for accurate comparison
 */
export function isStreakBroken(lastActivityDate: string): boolean {
  const lastActivityLocal = lastActivityDate.split("T")[0];

  // Calculate yesterday in local timezone
  const yesterday = new Date();
  yesterday.setDate(yesterday.getDate() - 1);
  const yesterdayLocal = `${yesterday.getFullYear()}-${String(yesterday.getMonth() + 1).padStart(2, "0")}-${String(yesterday.getDate()).padStart(2, "0")}`;

  // Compare as strings (YYYY-MM-DD format sorts correctly)
  return lastActivityLocal < yesterdayLocal;
}
